import VueRouter from 'vue-router'
import { Permission } from '@/modules/auth/models/Permission'
import { usePermissionStore } from '@/modules/auth/stores/PermissionStore'
import { useTeamStore } from '@/modules/team/stores/TeamStore'

export const PLANS_ROUTE_NAME = 'PlansAndBillingSettings'
export const APPSUMO_PLANS_ROUTE_NAME = 'AppsumoPlansAndBillingSettings'

/**
 * Single destination for every upgrade CTA in the app: the plans page owns the checkout hand-off, so
 * the CTAs themselves only navigate. Keeping the route choice and the two eligibility checks here
 * stops the ~14 call sites from each re-deriving them (and drifting apart, which is how we ended up
 * with three different upgrade behaviours).
 */

/**
 * AppSumo teams get their own plans route — the regular one is filtered out of the settings tabs for
 * them, mirroring the filter in TeamSettings.vue.
 */
function isAppsumoTeam(): boolean {
  return !!useTeamStore().currentTeam?.appsumoLicense
}

/**
 * The plans page is admin-gated (`UPDATE_TEAM_SETTINGS` on the route, which resolves to
 * `role === ADMIN`). `MANAGE_SUBSCRIPTIONS` resolves to the same role, so gating a CTA on this
 * guarantees it can never navigate a member into /403 — they get the contact-an-admin copy instead.
 */
export function canManagePlans(): boolean {
  return usePermissionStore().checkTeamPermission(Permission.MANAGE_SUBSCRIPTIONS)
}

/**
 * Whether this team has a plans page worth sending anyone to. Custom-pricing teams have the tab
 * filtered out and secret-referral teams render an empty overview, so both keep their existing
 * talk-to-sales / direct-payment-link behaviour rather than landing on a blank page.
 */
export function hasPlansPage(): boolean {
  const team = useTeamStore().currentTeam
  if (!team || team.secretReferral) {
    return false
  }
  if (isAppsumoTeam()) {
    return true
  }
  return !!team.subscription && !team.subscription.isCustomPricing
}

/** True when an upgrade CTA should be rendered at all. */
export function canShowUpgradeCta(): boolean {
  return canManagePlans() && hasPlansPage()
}

/**
 * vue-router 3 rejects a redundant navigation, which happens whenever a CTA is pressed while already
 * on the plans page — swallow it rather than surfacing an unhandled rejection.
 */
export function goToPlans(router: VueRouter): void {
  const name = isAppsumoTeam() ? APPSUMO_PLANS_ROUTE_NAME : PLANS_ROUTE_NAME
  router.push({ name }).catch(() => {})
}

/** For non-component callers (stores, interceptors) that have no `this.$router`. */
export async function goToPlansFromAnywhere(): Promise<void> {
  const router = (await import('@/router')).default
  goToPlans(router)
}
